import { Box, Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import type { TranslationKey } from "../../../../../../localization/translations"

import { isMandatoryAllergensMissing, isMandatoryFieldMissing, isMandatoryPhotoMissing } from "../../weekMenuLib"
import { WeekDishRow, WeekMenuCellState, WeekMenuCellValues } from "../../weekMenuTypes"
import { PlannerTag, PlannerTagVariant } from "../PlannerTag"

const useStyles = makeStyles()(() => ({
    badge: {
        display: "inline-flex",
        marginTop: ".5rem",
        alignSelf: "flex-start",
    },
    tooltipTitle: {
        fontWeight: 600,
    },
    tooltipList: {
        margin: ".25rem 0 0",
        paddingLeft: "1rem",
    },
}))

const STATUS_LABEL_IDS: Record<WeekMenuCellState, TranslationKey> = {
    [WeekMenuCellState.FILLED]: "merchant-week-menu-cell-status-ready",
    [WeekMenuCellState.NEEDS_FILL]: "merchant-week-menu-cell-status-missing",
    [WeekMenuCellState.EMPTY]: "merchant-week-menu-cell-status-optional",
    [WeekMenuCellState.TEMPLATE_DEFAULT]: "merchant-week-menu-cell-status-optional",
}

const STATUS_FALLBACKS: Record<WeekMenuCellState, string> = {
    [WeekMenuCellState.FILLED]: "Ready",
    [WeekMenuCellState.NEEDS_FILL]: "Missing",
    [WeekMenuCellState.EMPTY]: "Optional",
    [WeekMenuCellState.TEMPLATE_DEFAULT]: "Optional",
}

const STATUS_VARIANTS: Record<WeekMenuCellState, PlannerTagVariant> = {
    [WeekMenuCellState.FILLED]: PlannerTagVariant.READY,
    [WeekMenuCellState.NEEDS_FILL]: PlannerTagVariant.MISSING,
    [WeekMenuCellState.EMPTY]: PlannerTagVariant.OPTIONAL,
    [WeekMenuCellState.TEMPLATE_DEFAULT]: PlannerTagVariant.OPTIONAL,
}

type MissingFieldEntry = Readonly<{ labelId: TranslationKey; fallback: string }>

// Must stay in sync with the mandatory checks behind NEEDS_FILL, so the tooltip lists exactly
// the fields that keep the dish from being ready.
function missingFieldsFor(row: WeekDishRow, values: WeekMenuCellValues): MissingFieldEntry[] {
    const missingFields: MissingFieldEntry[] = []
    if (values.title.trim() === "") {
        missingFields.push({ labelId: "merchant-week-menu-missing-field-name", fallback: "Name" })
    }
    if (isMandatoryFieldMissing(row.description, values.description)) {
        missingFields.push({ labelId: "merchant-week-menu-missing-field-description", fallback: "Description" })
    }
    if (isMandatoryFieldMissing(row.price, values.price)) {
        missingFields.push({ labelId: "merchant-week-menu-missing-field-price", fallback: "Price" })
    }
    if (isMandatoryPhotoMissing(row.image, values.photo)) {
        missingFields.push({ labelId: "merchant-week-menu-missing-field-photo", fallback: "Photo" })
    }
    if (isMandatoryAllergensMissing(row.allergens, values.allergens)) {
        missingFields.push({ labelId: "merchant-week-menu-missing-field-allergens", fallback: "Allergens" })
    }
    if (isMandatoryFieldMissing(row.marketingTag, values.marketingTag)) {
        missingFields.push({ labelId: "merchant-week-menu-missing-field-marketing-tag", fallback: "Marketing tag" })
    }
    return missingFields
}

type Props = Readonly<{
    row: WeekDishRow
    values: WeekMenuCellValues
    state: WeekMenuCellState
}>

/** The cell's status tag; the Missing state explains itself with a tooltip of the unfilled fields. */
export function CellStatusTag({ row, values, state }: Props) {
    const { classes } = useStyles()

    const missingFields = state === WeekMenuCellState.NEEDS_FILL ? missingFieldsFor(row, values) : []

    return (
        <Tooltip
            arrow
            title={
                missingFields.length > 0 ? (
                    <Box>
                        <LocalizedStrict id="merchant-week-menu-missing-tooltip-title">
                            <Typography variant="caption" className={classes.tooltipTitle}>
                                Still missing
                            </Typography>
                        </LocalizedStrict>
                        <ul className={classes.tooltipList}>
                            {missingFields.map((field) => (
                                <li key={field.labelId}>
                                    <LocalizedStrict id={field.labelId}>
                                        <Typography variant="caption">{field.fallback}</Typography>
                                    </LocalizedStrict>
                                </li>
                            ))}
                        </ul>
                    </Box>
                ) : (
                    ""
                )
            }
        >
            <Box component="span" className={classes.badge}>
                <PlannerTag variant={STATUS_VARIANTS[state]}>
                    <LocalizedStrict id={STATUS_LABEL_IDS[state]}>
                        <span>{STATUS_FALLBACKS[state]}</span>
                    </LocalizedStrict>
                </PlannerTag>
            </Box>
        </Tooltip>
    )
}
